<template>
  <DashboardCard v-if="projectTrackingEnabled" :title="$t('Project tracker')">
    <div>
      <WdsStack v-if="projects.length > 0" justify-space-between class="tw-pb-1 tw-z-100">
        <WdsMenu placement="bottom-end" manual-trigger z-index="50">
          <template #trigger="{ toggle }">
            <WdsButtonGhost @click="toggle">
              <WdsStack justify-center align-center gap-sm>
                <span>{{ $t(selectedSort.label) }}</span>
                <span><WdsIcon :size="16" :name="WdsIconNameEnum.CHEVRON_DOWN"></WdsIcon></span>
              </WdsStack>
            </WdsButtonGhost>
          </template>
          <WdsMenuOption
            v-for="option in sortOptions"
            :key="option.label"
            :active="selectedSort.label === option.label"
            @click="updateSort(option)"
          >
            {{ option.label }}
            <WdsIcon
              v-if="selectedSort.label === option.label"
              :name="WdsIconNameEnum.CHECKMARK"
              :size="16"
              color="#5d36f1"
            />
          </WdsMenuOption>
        </WdsMenu>
        <a :href="laravelNamedRoute('projects.index')">
          <WdsButtonGhost secondary class="tw-items-center">
            {{ $t('See all') }}
            <WdsIcon :size="16" :name="WdsIconNameEnum.CHEVRON_RIGHT" />
          </WdsButtonGhost>
        </a>
      </WdsStack>

      <WdsStack gap-sm direction-column>
        <div v-if="loading" class="loading-list">
          <LoadingList :count="3" />
        </div>

        <WdsStack v-else-if="projects.length === 0" align-center justify-center class="tw-pt-5 tw-pb-8">
          <WdsText secondary>{{ $t('No projects found') }}</WdsText>
        </WdsStack>

        <div v-else class="project-list">
          <div v-for="project in projects" :key="project.id">
            <WdsStack justify-space-between class="project-container">
              <a
                class="project tw-w-full tw-h-full"
                :href="laravelNamedRoute('projects.show', { project: project.id })"
              >
                <WdsStack direction="row">
                  <ProgressBar
                    class="tw-w-full"
                    :label="project.name"
                    :spent="project.spent"
                    :budget="project.internalBudget"
                    :allocated="project.allocatedBudget"
                    :currency="project.currency"
                  />
                  <WdsStack align-center justify-center class="tw-ps-5">
                    <WdsIcon :size="16" :name="WdsIconNameEnum.CHEVRON_RIGHT" class="tw-text-gray-800" />
                  </WdsStack>
                </WdsStack>
              </a>
            </WdsStack>
          </div>
        </div>
      </WdsStack>
    </div>
  </DashboardCard>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsStack,
  WdsText,
  WdsMenu,
  WdsIcon,
  WdsMenuOption,
  WdsIconNameEnum
} from '@worksome/wds-components'
import { ref, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import ProgressBar from '@/components/Projects/ProgressBar.vue'
import { ProjectStatusFilter } from '@/gql/graphql'

const sortOptions = [
  { label: $t('Most budget used'), order: 'DESC' },
  { label: $t('Least budget used'), order: 'ASC' },
]

const projectTrackingEnabled = computed(() => currentAccount?.settings?.project_tracking)

const selectedSort = ref(sortOptions[0])

const queryVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  owners: [currentUser.globalId],
  status: [ProjectStatusFilter.Open],
}))

const { result, loading } = useQuery(
  graphql(`
    query ProjectsList($accounts: [ID!], $owners: [ID!], $status: [ProjectStatusFilter!]) {
      projects(accounts: $accounts, owners: $owners, status: $status, first: 1000) {
        data {
          id
          name
          currency
          internalBudget
          allocatedBudget
          spent
        }
      }
    }
  `),
  queryVariables,
  {
    clientId: 'publicApiCustomFields',
  },
)

const calculateBudgetScore = (project) => {
  if (!project.internalBudget || project.internalBudget === 0) return 0

  const hasSpentAmount = project.spent && project.spent > 0

  const percentage =
    ((hasSpentAmount ? project.spent : project.allocatedBudget) / project.internalBudget) * 100

  // Creates a compound score to ensure spent amounts are always prioritised over allocated amounts.
  return (hasSpentAmount ? 100000 : 0) + percentage
}

const projects = computed(() => {
  const data = result.value?.projects.data ?? []
  return [...data].sort((a, b) => {
    const aScore = calculateBudgetScore(a)
    const bScore = calculateBudgetScore(b)
    return selectedSort.value.order === 'DESC' ? bScore - aScore : aScore - bScore
  })
})

const updateSort = (option) => {
  selectedSort.value = option
}
</script>

<style lang="scss" scoped>
.project-list {
  @apply tw-mt-1;
  @apply tw-min-h-8;
  @apply tw-max-h-[290px];
  @apply tw-overflow-y-auto;
  @apply tw-relative;
  @apply tw-pb-2;
}

.project-container {
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-py-1;
  @apply tw-pl-1;
  @apply tw-pr-5;
  @apply tw-mb-2;
  @apply tw-border-2;
  @apply tw-border-transparent;
  @apply tw-transition;
  @apply tw-duration-200;
}

.project-container:hover {
  @apply tw-border-2;
  @apply tw-border-gray-200;
}

.project {
  @apply tw-text-left;
  @apply tw-p-1;
  @apply tw-px-2;
}

.loading-list {
  @apply tw-pb-4;
}
</style>
